<script setup lang="ts">
// -------------------------------
// 🔹 BaseToast
// -------------------------------
/**
 * Composant de notification (toast) pour afficher les messages de succès ou d'erreur.
 *
 * Props :
 * - IBaseToastProps : informations du toast (message, type, durée, etc.)
 *
 * Événements :
 * - "handleError" : émis lorsqu'une action d'erreur est déclenchée
 * - "handleSuccess" : émis lorsqu'une action de succès est déclenchée
 *
 * Méthodes internes :
 * - HandleError() : émet l'événement "handleError"
 * - HandleSuccess() : émet l'événement "handleSuccess"
 * - toogleError() : réinitialise l'état global d'erreur et le loader du formulaire
 */
import { watch } from 'vue';
import { isGlobalError, isGlobalSucess } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';
import type { IBaseToastProps } from '@/types/base_toast.types';

const props = defineProps<IBaseToastProps>();
const emit = defineEmits(['handleError', 'handleSuccess']);

const HandleError = () => {
  toogleError();
  emit('handleError');
};
const HandleSuccess = () => {
  toogleSuccess();
  emit('handleSuccess');
};

const toogleError = () => {
  isGlobalError.value = false;
  isLoaderLoginFormActivated.value = false;
};
const toogleSuccess = () => {
  isGlobalSucess.value = false;
};

const AUTO_DISMISS_MS = 4000;
let errorTimer: ReturnType<typeof setTimeout> | null = null;
let successTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.isError,
  (open) => {
    if (errorTimer) clearTimeout(errorTimer);
    if (open) errorTimer = setTimeout(toogleError, AUTO_DISMISS_MS);
  },
);
watch(
  () => props.isSucceed,
  (open) => {
    if (successTimer) clearTimeout(successTimer);
    if (open) successTimer = setTimeout(toogleSuccess, AUTO_DISMISS_MS);
  },
);
</script>

<template>
  <!-- Toast Erreur -->
  <transition
    enter-active-class="transition duration-500 ease-out transform"
    enter-from-class="-translate-y-10 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-500 ease-in transform"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-10 opacity-0"
  >
    <div
      v-show="isError"
      @click="toogleError"
      class="fixed top-5 right-5 z-[70000] flex w-72 items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 shadow-lg"
    >
      <svg
        class="h-6 w-6 cursor-pointer text-red-600 transition-transform hover:scale-110"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        @click.stop="HandleError"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      <p class="text-sm font-medium text-red-600">{{ errorMessage }}</p>
    </div>
  </transition>

  <!-- Toast Succès -->
  <transition
    enter-active-class="transition duration-500 ease-out transform"
    enter-from-class="-translate-y-10 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-500 ease-in transform"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-10 opacity-0"
  >
    <div
      v-show="isSucceed"
      @click="toogleSuccess"
      class="fixed top-5 right-5 z-[70000] flex w-72 items-center gap-3 rounded-xl border border-green-300 bg-green-50 px-4 py-3 shadow-lg"
    >
      <svg
        class="h-6 w-6 cursor-pointer text-green-600 transition-transform hover:scale-110"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        @click.stop="HandleSuccess"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      <p class="text-sm font-medium text-green-600">{{ succesMessage }}</p>
    </div>
  </transition>
</template>
